import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { keyBy, toArray } from 'lodash-es';
import { catchError, of, pipe, switchMap, tap } from 'rxjs';

import { LandingPage, LandingPages } from '@@core/models/landing-page.model';
import { LaunchDarklyService, LDFeatureKey } from '@@core/services/launch-darkly.service';
import {
	APP_CONFIG,
	initialApplicationConfig,
} from '@@shared/providers/application-config-provider/application-config-provider.model';

export const ConfigurationStore = signalStore(
	{ providedIn: 'root' },
	withState(() => ({ ...initialApplicationConfig, ...inject(APP_CONFIG) })),
	withComputed(
		(
			store,
			launchDarklyService = inject(LaunchDarklyService),
			isInitialized = inject(LaunchDarklyService).ldInitializedSignal$,
		) => ({
			landingPagesSignal$: computed(() => {
				if (!isInitialized()) {
					// This is here to retrigger the computed when LD is initialized
					return {};
				}
				const customTabPages: Record<string, LandingPage> = {};
				store.customTabs()?.forEach(tab => {
					const normalizedTabName = tab.name.split(' ').join('_').toLowerCase();
					const key = `custom_tab_${normalizedTabName}`;
					customTabPages[key] = {
						name: tab.name,
						value: key,
						route: '/custom-tab',
						queryParams: {
							url: tab.url,
							name: tab.name,
							type: 'iframe',
						},
					};
				});

				const filteredPages = keyBy(
					toArray(LandingPages).filter(
						c => !c.featureFlag || launchDarklyService.getRawFeatureFlagValue(c.featureFlag),
					),
					'value',
				);

				return {
					...filteredPages,
					...customTabPages,
				};
			}),
		}),
	),
	withMethods((store, resetState = inject(APP_CONFIG), launchDarklyService = inject(LaunchDarklyService)) => ({
		reset: (): void => {
			patchState(store, () => ({ ...initialApplicationConfig, ...resetState }));
		},

		loadCustomerConfig: rxMethod<void>(
			pipe(
				switchMap(() => launchDarklyService.getJsonObjectForKey(LDFeatureKey.CUSTOMER_WEB_APP_CONFIG)),
				tap(config => patchState(store, state => ({ ...state, ...(config || {}) }))),
				catchError(err => of({})),
			),
		),
	})),
);
